import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  LegendProps,
} from 'recharts'
import { Text } from '@/ui'
import {
  centsToDollars,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  CATEGORY_FILTERS,
  ChartCategories,
  INSIGHTS_TYPE,
  getBarColor,
  insightsCopyConstants,
} from './utils'
import { includes } from 'lodash'
import { categoryColorMap } from './categoryColorMap'
// TODO: Migrate V2
import { Colors } from '../../../styles/theme'
import { TypographyStyles } from '@/styles'

const formatLabel = (value: string, categories?: ChartCategories): string => {
  return categories?.[value]?.category_name || value
}

const getTooltipTopLabel = (type: INSIGHTS_TYPE, filter?: string) => {
  if (type === INSIGHTS_TYPE.PROFIT) return ''
  if (!filter || filter === CATEGORY_FILTERS.ALL) {
    return `Top 3 ${insightsCopyConstants[type].label}`
  } else if (filter === CATEGORY_FILTERS.TOP) {
    return `Top ${insightsCopyConstants[type].label}`
  } else if (filter === CATEGORY_FILTERS.DEDUCTIBLE) {
    return 'Top Deductible Expenses'
  }
  return ''
}

const CustomTooltip = ({
  active,
  payload,
  filter,
  categories,
  type,
}: TooltipProps<number, string> & {
  filter?: string
  categories?: ChartCategories
  type: INSIGHTS_TYPE
}) => {
  if (active && payload?.length === 0) {
    return (
      <div className="bg-neutral-0 p-3 rounded text-center visible max-w-[140px] shadow-[1px_2px_20px_1px_rgba(0,0,0,0.25)]">
        <Text>{insightsCopyConstants[type].emptyChartMonth}</Text>
      </div>
    )
  } else if (active && payload?.length) {
    const totalAmount = payload.reduce(
      (sum, entry) => sum + (entry.value as number),
      0
    )

    const sortedPayload = payload
      .slice()
      .sort((a, b) => (b.value || 0) - (a.value || 0))

    const dateString = `${payload[0]?.payload?.month} ${payload[0]?.payload?.year}`

    const renderHeaders = includes(Object.values(CATEGORY_FILTERS), filter)

    return (
      <div className="bg-neutral-0 p-3 rounded text-center shadow-[1px_2px_20px_1px_rgba(0,0,0,0.25)]">
        <Text variant={TypographyStyles.H3} className="mb-3">
          {dateString}
        </Text>
        {renderHeaders && (
          <>
            <Text variant={TypographyStyles.H3} className="mb-3">
              {formatCurrencyFromCents(totalAmount)}
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              <b>{getTooltipTopLabel(type, filter)}</b>
            </Text>
          </>
        )}
        {type === INSIGHTS_TYPE.PROFIT && (
          <>
            <Text variant={TypographyStyles.BODY_SM}>
              Income:{' '}
              {formatCurrencyFromCents(payload[0]?.payload?.total_income)}
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              Expenses:{' '}
              {formatCurrencyFromCents(
                Math.abs(payload[0]?.payload?.total_expenses)
              )}
            </Text>
          </>
        )}
        {type !== INSIGHTS_TYPE.PROFIT &&
          sortedPayload
            .slice(0, 3)
            .map((entry) => (
              <Text
                key={entry.name}
                variant={TypographyStyles.BODY_SM}
              >{`${formatLabel(
                entry.name || '',
                categories
              )}: ${formatCurrencyFromCents(entry.value)}`}</Text>
            ))}
      </div>
    )
  }

  return null
}

const amountFormatter = (num: number) => {
  return `$${centsToDollars(num).toLocaleString()}`
}

const CustomLegend = ({
  payload,
  categories,
}: LegendProps & { categories: ChartCategories }) => {
  return (
    <div className="flex flex-col gap-3">
      {payload?.map((entry) => (
        <div
          key={`legend-${entry.value}`}
          className="flex items-center mr-2 gap-2"
        >
          <div
            className="w-3 h-3 rounded-sm"
            style={{
              backgroundColor: entry.color,
            }}
          />
          <span className="text-neutral-900">
            {formatLabel(entry.value, categories)}
          </span>
        </div>
      ))}
    </div>
  )
}

export const InsightsChart = ({
  data,
  categories,
  filter,
  isChartDataEmpty,
  filteredCategories,
  type,
}: {
  data: {
    [key: string]: string | number | undefined
  }[]
  categories: ChartCategories
  filter?: string
  isChartDataEmpty: boolean
  filteredCategories: ChartCategories
  type: INSIGHTS_TYPE
}) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  return (
    <div className="relative">
      {isChartDataEmpty && (
        <Text
          variant={TypographyStyles.H3}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-neutral-500"
        >
          {insightsCopyConstants[type].emptyChart}
        </Text>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data} margin={{ left: isMobile ? 4 : 24 }}>
          <CartesianGrid horizontal vertical={false} />
          <XAxis dataKey="month" />
          <YAxis
            tickFormatter={amountFormatter}
            dataKey={type === INSIGHTS_TYPE.PROFIT ? 'profit' : undefined}
          />
          <Tooltip
            cursor={{ fill: Colors.stone40 }}
            content={
              <CustomTooltip
                filter={filter}
                categories={categories}
                type={type}
              />
            }
          />
          <Legend
            formatter={(value, _entry) => formatLabel(value, categories)}
            layout={isMobile ? 'horizontal' : 'vertical'}
            align={isMobile ? undefined : 'right'}
            verticalAlign={isMobile ? undefined : 'middle'}
            content={<CustomLegend categories={categories} />}
            wrapperStyle={{
              paddingLeft: 20,
              height: isMobile ? '20%' : '80%',
              overflow: 'auto',
              lineHeight: 1.7,
            }}
          />
          {Object.entries(filteredCategories).map(([key, category]) => (
            <Bar
              key={category.id}
              dataKey={key}
              stackId="a"
              fill={categoryColorMap[key] || getBarColor(category.id)}
              barSize={isMobile ? 16 : 24}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
